import { useEffect, useRef, useState } from "react";
import { FormInput } from "./FormInput";
import { useUserLocale } from "@/hooks/useUserLocale";
import {
  formatIntegerGroupedLive,
  stripIntegerGrouping,
  useLiveGroupedInput,
} from "@/lib/utils/number-input-format";

type GroupedNumberInputProps = Omit<
  React.ComponentProps<typeof FormInput>,
  "type" | "value" | "onChangeAction" | "ref"
> & {
  /** Canonical integer string (no grouping), e.g. "10000". */
  value: string;
  /** Emits the canonical integer string (no grouping) — "" when cleared. */
  onChangeAction: (canonical: string) => void;
  /** Max number of digits accepted (excluding sign). */
  maxLengthDigits?: number;
};

/**
 * A thin `FormInput` wrapper that shows large integers with live thousand-
 * separators ("10000" → "10.000") while emitting a plain canonical digit string
 * — a drop-in for the old `type="number"` count fields whose handlers already
 * `parseInt` the value. Caret is preserved by {@link useLiveGroupedInput}.
 */
export const GroupedNumberInput: React.FC<GroupedNumberInputProps> = ({
  value,
  onChangeAction,
  maxLengthDigits = 15,
  inputProps,
  ...rest
}) => {
  const locale = useUserLocale();
  const inputRef = useRef<HTMLInputElement>(null);
  const formatLive = useLiveGroupedInput({ inputRef, locale, mode: "integer" });
  const editableOf = (v: string) =>
    v == null || v === "" || isNaN(Number(v))
      ? ""
      : formatIntegerGroupedLive(v, locale);
  const [draft, setDraft] = useState(() => editableOf(value));
  // PB-TK-2: the canonical string this input last emitted upward. See the
  // effect below — without it, typing "0" made the digit vanish.
  const lastEmittedRef = useRef<string | null>(null);

  useEffect(() => {
    // PB-TK-2 — do NOT re-seed the draft from a value the parent merely echoed
    // back at us.
    //
    // Callers hold a plain `number` and render `String(limit || "")`, which
    // collapses BOTH "field cleared" and "user typed 0" to the empty string.
    // So typing `0` drove the parent to 0, `value` recomputed to "", and this
    // effect wiped the digit the user had just pressed. Select-all + `0` was
    // simply impossible: the character disappeared as you typed it.
    //
    // Fixing the callers alone cannot work — with `number` there is no value
    // that distinguishes empty from zero, so the ambiguity is structural. The
    // guard instead asks whether the incoming value is NUMERICALLY equivalent
    // to what we last sent up (treating "" as 0, exactly the collapse the
    // callers perform). If it is, the parent has not actually decided
    // anything new and the user's in-progress text is the better truth.
    //
    // A genuinely external change — loading saved settings, a reset button —
    // is not equivalent to our last emit, so it still re-seeds normally.
    const numeric = (v: string) => {
      if (v == null || v === "") return 0;
      const n = Number(v);
      return isNaN(n) ? 0 : n;
    };
    if (
      lastEmittedRef.current !== null &&
      numeric(value) === numeric(lastEmittedRef.current)
    ) {
      return;
    }
    lastEmittedRef.current = null;
    setDraft(editableOf(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, locale]);

  return (
    <FormInput
      {...rest}
      ref={inputRef}
      type="text"
      inputProps={{ inputMode: "numeric", ...(inputProps ?? {}) }}
      value={draft}
      onChangeAction={(raw) => {
        const canonical = stripIntegerGrouping(raw, locale);
        if (canonical.replace("-", "").length > maxLengthDigits) return;
        setDraft(formatLive(raw));
        lastEmittedRef.current = canonical;
        onChangeAction(canonical);
      }}
    />
  );
};
